import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp, Globe2, Award, Calculator } from "lucide-react";
import { fetchSiteSettings } from "../../lib/siteSettings";

const PROOF_DEFAULTS = {
  proof_heading: "Built for Fortune 1000s.",
  proof_subheading:
    "12+ years of B2B specialisation. Senior-only delivery with programs running across 8 countries.",
  proof_tile_1_value: "12+",
  proof_tile_1_label: "Years in B2B",
  proof_tile_2_value: "98%",
  proof_tile_2_label: "Avg pipeline lift",
  proof_tile_3_value: "8",
  proof_tile_3_label: "Countries served",
};

const IMG = {
  strategy:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2000&auto=format&fit=crop",
  workshop:
    "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=1600&auto=format&fit=crop",
  collab:
    "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1600&auto=format&fit=crop",
  client:
    "https://images.unsplash.com/photo-1551836022-deb4988cc6c0?q=80&w=1600&auto=format&fit=crop",
  hands:
    "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?q=80&w=1600&auto=format&fit=crop",
};

const chip = (label, color) => ({
  label,
  color,
});

export default function HumansBehind() {
  const [proof, setProof] = useState(PROOF_DEFAULTS);

  useEffect(() => {
    fetchSiteSettings().then((data) => {
      if (!data) return;
      setProof((p) => ({
        ...p,
        ...Object.fromEntries(
          Object.keys(PROOF_DEFAULTS)
            .filter((k) => data[k] != null && String(data[k]).trim() !== "")
            .map((k) => [k, data[k]])
        ),
      }));
    });
  }, []);

  return (
    <section
      data-testid="humans-behind"
      className="section-light py-section relative overflow-hidden"
    >
      <div className="bg-orbs bg-orbs-alt" />
      <div className="container-xl relative">
        <div className="max-w-3xl mb-14 md:mb-20 text-center mx-auto">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#86868B] mb-5">
            The humans behind the pipeline
          </div>
          <h2 className="font-display text-[#1D1D1F] text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-semibold">
            People, not{" "}
            <span className="font-serif italic font-light text-[#86868B]">
              dashboards.
            </span>
          </h2>
          <p className="mt-6 text-[#515154] text-base md:text-lg leading-relaxed">
            AI-led pipelines still need editors, strategists, and operators.
            Meet the small, senior team your program gets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(220px,auto)]">
          {/* Big left card */}
          <motion.div
            data-testid="humans-card-1"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 md:row-span-2 relative rounded-[32px] overflow-hidden bg-black group card-lift"
          >
            <img
              src={IMG.strategy}
              alt="Strategy session"
              className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:scale-[1.04] transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
            <div className="absolute inset-0 p-8 md:p-12 flex flex-col justify-between text-white">
              <Chip label="Strategy" color="#FFB000" />
              <div>
                <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] font-medium max-w-xl">
                  Senior editors. Senior strategists.{" "}
                  <span className="font-serif italic font-light">No juniors.</span>
                </h3>
                <p className="mt-5 text-white/75 text-base md:text-lg max-w-md leading-relaxed">
                  Every program is steered by a senior lead — 10+ years of B2B
                  demand, no handoffs to juniors, no outsourced writing.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Top right */}
          <motion.div
            data-testid="humans-card-2"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative rounded-[32px] overflow-hidden bg-black group card-lift min-h-[280px]"
          >
            <img
              src={IMG.workshop}
              alt="Workshop"
              className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:scale-[1.04] transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between text-white">
              <Chip label="Workshops" color="#3BA7D1" />
              <div>
                <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
                  We co-design your ICP.
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-xs">
                  Intent models and buying-mode frameworks, built in-room with
                  your team.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Mid right — replaces 37-person / Mumbai, NY, Singapore card.
              Instead: factual proof tiles. No speculative team size, no unverifiable cities. */}
          <motion.div
            data-testid="humans-card-3"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 relative rounded-[32px] overflow-hidden group card-lift bg-gradient-to-br from-[#FFB000]/15 via-white to-[#3BA7D1]/15 border border-black/5 min-h-[280px]"
          >
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.35),transparent_60%)] blur-3xl" />
            <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-between">
              <Chip label="The track record" color="#B87500" dark />
              <div>
                <h3 className="font-display text-[#1D1D1F] text-2xl md:text-3xl tracking-tight font-medium">
                  {proof.proof_heading}
                </h3>
                <p className="mt-3 text-[#515154] text-sm leading-relaxed max-w-sm">
                  {proof.proof_subheading}
                </p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <ProofTile
                    Icon={Award}
                    value={proof.proof_tile_1_value}
                    label={proof.proof_tile_1_label}
                    tint="#B87500"
                  />
                  <ProofTile
                    Icon={TrendingUp}
                    value={proof.proof_tile_2_value}
                    label={proof.proof_tile_2_label}
                    tint="#0FB5A6"
                  />
                  <ProofTile
                    Icon={Globe2}
                    value={proof.proof_tile_3_value}
                    label={proof.proof_tile_3_label}
                    tint="#3BA7D1"
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bottom full width row */}
          <motion.div
            data-testid="humans-card-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 relative rounded-[32px] overflow-hidden bg-black group card-lift min-h-[280px]"
          >
            <img
              src={IMG.collab}
              alt="Collaboration"
              className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:scale-[1.04] transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between text-white">
              <Chip label="Pairing" color="#0FB5A6" />
              <div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium">
                  Paired with your team.
                </h3>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">
                  Weekly syncs, shared dashboards, a single Slack channel.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            data-testid="humans-card-5"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 relative rounded-[32px] overflow-hidden bg-black group card-lift min-h-[280px]"
          >
            <img
              src={IMG.client}
              alt="Client session"
              className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:scale-[1.04] transition-transform duration-[1200ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute inset-0 p-7 md:p-8 flex flex-col justify-between text-white">
              <Chip label="Enterprise" color="#6B77E8" />
              <div>
                <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium">
                  Quarterly on-site.
                </h3>
                <p className="mt-2 text-white/70 text-sm leading-relaxed">
                  We show up. Strategy offsites, stakeholder alignment, fieldwork.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.a
            href="/#calculator"
            data-testid="humans-card-cta"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-4 group card-lift relative rounded-[32px] overflow-hidden bg-[#0A0A0A] text-white p-8 md:p-10 flex flex-col justify-between min-h-[280px] border border-white/10"
          >
            <div className="absolute -bottom-24 -right-24 w-[320px] h-[320px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.4),transparent_60%)] blur-3xl" />
            <div className="absolute -top-24 -left-24 w-[260px] h-[260px] rounded-full bg-[radial-gradient(circle,rgba(59,167,209,0.25),transparent_60%)] blur-3xl" />
            <Chip label="Pipeline math" color="#FFB000" />
            <div className="relative">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="h-9 w-9 rounded-xl bg-[#FFB000]/20 border border-[#FFB000]/40 flex items-center justify-center">
                  <Calculator size={16} strokeWidth={2} color="#FFB000" />
                </div>
                <span className="text-[11px] uppercase tracking-[0.22em] text-white/55">
                  60 seconds · live
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
                See your pipeline,{" "}
                <span className="font-serif italic font-light text-white/70">
                  numbers & all.
                </span>
              </h3>
              <p className="mt-3 text-white/60 text-sm leading-relaxed max-w-xs">
                Plug in traffic, ACV, and cycle — see MQL/SQL, ARR, and the
                3 plays that close the gap.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-[#FFB000] text-sm font-medium">
                Run the calculator
                <ArrowUpRight size={14} strokeWidth={2.2} />
              </div>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}

function Chip({ label, color, dark }) {
  return (
    <span
      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] border rounded-full px-3 py-1.5 backdrop-blur-md self-start"
      style={{
        background: dark ? `${color}18` : `${color}28`,
        borderColor: `${color}55`,
        color: dark ? color : color,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function ProofTile({ Icon, value, label, tint }) {
  return (
    <div
      className="rounded-2xl bg-white/80 backdrop-blur-sm border border-black/5 p-3 flex flex-col items-start gap-2"
      style={{ borderLeft: `3px solid ${tint}` }}
    >
      <Icon size={14} strokeWidth={2} color={tint} />
      <div>
        <div
          className="font-display text-[#1D1D1F] tracking-tight font-semibold leading-none"
          style={{ fontSize: "22px", letterSpacing: "-0.02em" }}
        >
          {value}
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.14em] text-black/50 leading-tight">
          {label}
        </div>
      </div>
    </div>
  );
}
